import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Application, AwardCommitteeMeeting, Kpi } from '@tran-monorepo/interfaces/applications';
import { EMPTY, finalize, forkJoin, map, Observable, tap } from 'rxjs';
import { ApplicationsService } from '../../../../shared/services/applications.service';
import { AwardCommitteeService } from '../../../../shared/services/award-committee.service';
import { CustomerApplicationDetailStateModel } from './application-detail-state.model';
import {
    LoadApplication,
    LoadSelectedAwardCommitteeMeeting,
    ResetApplicationDetailState
} from './application-detail.action';

const DEFAULT_STATE: CustomerApplicationDetailStateModel = {
    application: {
        value: null,
        isLoading: false
    },
    applicationKpis: [],
    selectedAwardCommitteeMeeting: {
        meeting: null,
        isLoading: false
    }
};

@State<CustomerApplicationDetailStateModel>({
    name: 'customerApplicationDetailState',
    defaults: DEFAULT_STATE
})
@Injectable()
export class CustomerApplicationDetailState {
    private readonly applicationService = inject(ApplicationsService);
    private readonly awardCommitteeService = inject(AwardCommitteeService);

    @Selector()
    public static application(state: CustomerApplicationDetailStateModel): Application | null {
        return state.application.value;
    }

    @Selector()
    public static isLoadingApplication(state: CustomerApplicationDetailStateModel): boolean {
        return state.application.isLoading;
    }

    @Selector()
    public static applicationKpis(state: CustomerApplicationDetailStateModel): Kpi[] {
        return state.applicationKpis;
    }

    @Selector()
    public static selectedAwardCommitteeMeeting(
        state: CustomerApplicationDetailStateModel
    ): AwardCommitteeMeeting | null {
        return state.selectedAwardCommitteeMeeting.meeting;
    }

    @Selector()
    public static isSelectedAwardCommitteeMeetingLoading(state: CustomerApplicationDetailStateModel): boolean {
        return state.selectedAwardCommitteeMeeting.isLoading;
    }

    @Action(LoadApplication)
    public loadApplications(
        { setState }: StateContext<CustomerApplicationDetailStateModel>,
        { applicationId }: LoadApplication
    ): Observable<Application> {
        setState(
            patch<CustomerApplicationDetailStateModel>({
                application: patch({
                    isLoading: true
                })
            })
        );
        return forkJoin({
            application: this.applicationService.getApplication(applicationId),
            applicationKpis: this.applicationService.getApplicationKpis(applicationId)
        }).pipe(
            tap(({ application, applicationKpis }) =>
                setState(
                    patch<CustomerApplicationDetailStateModel>({
                        application: patch({
                            value: application
                        }),
                        applicationKpis
                    })
                )
            ),
            map(({ application }) => application),
            finalize(() =>
                setState(
                    patch<CustomerApplicationDetailStateModel>({
                        application: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(LoadSelectedAwardCommitteeMeeting)
    public loadSelectedAwardCommitteeMeeting({
        setState,
        getState
    }: StateContext<CustomerApplicationDetailStateModel>): Observable<AwardCommitteeMeeting> {
        const {
            application: { value: application }
        } = getState();
        if (!application?.awardCommitteeMeetingId) {
            return EMPTY;
        }
        setState(
            patch<CustomerApplicationDetailStateModel>({
                selectedAwardCommitteeMeeting: patch({
                    isLoading: true
                })
            })
        );
        return this.awardCommitteeService.getMeetingById(application.awardCommitteeMeetingId).pipe(
            tap(meeting =>
                setState(
                    patch<CustomerApplicationDetailStateModel>({
                        selectedAwardCommitteeMeeting: patch({
                            meeting
                        })
                    })
                )
            ),
            finalize(() =>
                setState(
                    patch<CustomerApplicationDetailStateModel>({
                        selectedAwardCommitteeMeeting: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(ResetApplicationDetailState)
    public resetApplicationDetailState({ patchState }: StateContext<CustomerApplicationDetailStateModel>): void {
        patchState(DEFAULT_STATE);
    }
}
